import {
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { Fragment } from 'react'
import { useSelector } from 'react-redux'
import { selectLoadingBulkTemplateFromState } from 'src/app/CreateVirtualCardForm/redux/createVirtualCardForm.selectors'
import Box from 'src/components/Box/Box'
import CreditCardDropdown, {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import CsvInputField from 'src/components/CsvInputField/CsvInputField'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import VirtualCardPushTaskTable from 'src/components/VirtualCardPushTaskTable/VirtualCardPushTaskTable'
import { ExtendCurrency, VirtualCardTemplateTypes } from 'src/enums'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { VirtualCardPushTaskListModel } from 'src/models/virtualCardPushTask.models'
import { faqUrl } from 'src/navigation/urls'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectBrandFeaturesFromState } from 'src/redux/selectors/session.selectors'
import { FormProps } from 'src/types'
import { css } from 'styled-components'

interface CreditCardAndCsvBoxProps {
  id?: string
  labelCreditCardsAsAccounts: boolean
  creditCardOptions: CreditCardOption[]
  className?: string
  creditCardIdProps: FormProps<string>
  csvProps: FormProps<File>
  currency: ExtendCurrency
  downloadTemplate: (type: VirtualCardTemplateTypes) => void
  showTaskTable: boolean
  virtualCardPushTasks?: VirtualCardPushTaskListModel[]
  creditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps
  isBillPay?: boolean
}

const CreditCardAndCsvBox: React.FC<CreditCardAndCsvBoxProps> = (
  props,
): React.ReactElement => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const { recurringVirtualCardsEnabled } = useSelector(
    selectBrandFeaturesFromState,
  )

  const loadingBulkTemplate = useSelector(selectLoadingBulkTemplateFromState)

  const { data: user } = useQueryOwnUser({})
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled

  const ccId =
    props.creditCardIdProps?.value !== undefined
      ? props.creditCardIdProps.value
      : props.creditCardOptions?.length === 1
        ? props.creditCardOptions[0].value
        : undefined
  const creditCardIdSet =
    ccId !== undefined ? new Set([ccId]) : new Set<string>()
  const ccOption =
    ccId !== undefined
      ? props.creditCardOptions.find((cc) => cc.value === ccId)
      : undefined
  const selectedOptions = ccOption !== undefined ? [ccOption] : undefined

  const budgetOptions = props.creditCardOptions?.filter(
    (cc) => cc.details?.creditCard.isBudget,
  )
  const rcnOptions = props.creditCardOptions?.filter(
    (cc) => !cc.details?.creditCard.isBudget,
  )

  const downloadTemplateOptions = []
  if (props.isBillPay) {
    downloadTemplateOptions.push({
      key: VirtualCardTemplateTypes.BillPay,
      labelSrc: 'Bill Pay',
      onClick: (): void =>
        props.downloadTemplate(VirtualCardTemplateTypes.BillPay),
      testId: 'billPayTemplateOption',
    })
  } else {
    downloadTemplateOptions.push({
      key: VirtualCardTemplateTypes.Standard,
      labelSrc: 'Standard virtual cards',
      onClick: (): void =>
        props.downloadTemplate(VirtualCardTemplateTypes.Standard),
      testId: 'standardTemplateOption',
    })
    if (recurringVirtualCardsEnabled) {
      downloadTemplateOptions.push({
        key: VirtualCardTemplateTypes.Recurring,
        labelSrc: 'Recurring virtual cards',
        onClick: (): void =>
          props.downloadTemplate(VirtualCardTemplateTypes.Recurring),
        testId: 'recurringTemplateOption',
      })
    }
    if (!showGuestRole) {
      downloadTemplateOptions.push({
        key: VirtualCardTemplateTypes.Guest,
        labelSrc: 'Guest virtual cards',
        onClick: (): void =>
          props.downloadTemplate(VirtualCardTemplateTypes.Guest),
        testId: 'guestTemplateOption',
      })
    }
  }

  return (
    <Box
      id={props?.id}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <span
        css={css`
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
        `}
      >
        <Trns source={'Choose source and template'} />
      </span>
      <span
        css={css`
          margin-top: 8px;
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        {props.isBillPay ? (
          <Trns
            source={
              'Use the template to upload a CSV file and create up to 100 unique virtual cards. Virtual cards with a vendor email will be automatically sent to the vendor once created.'
            }
          />
        ) : (
          <Trns
            source={
              'Using the appropriate template (standard, recurring, or guest), you can create up to 100 unique virtual cards at a time. ${boldText}'
            }
            expressions={{
              boldText: (
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  <Trns
                    source={
                      'Recipients must already be in your org in order to receive standard or recurring cards.'
                    }
                  />
                </span>
              ),
            }}
          />
        )}
      </span>
      <CreditCardDropdown
        css={css`
          margin-top: 40px;
        `}
        fieldLabel={<Trns source={'Funding Source'} />}
        errors={props.creditCardIdProps.errors}
        optionGroups={[
          ...(budgetOptions !== undefined && budgetOptions.length > 0
            ? [
                {
                  label: <Trns source={'Budgets'} />,
                  options: budgetOptions,
                },
              ]
            : []),
          ...(rcnOptions !== undefined && rcnOptions.length > 0
            ? [
                {
                  label: <Trns source={'Accounts'} />,
                  options: rcnOptions,
                },
              ]
            : []),
        ]}
        chooseOption={props.creditCardIdProps.onChange}
        selectedValues={creditCardIdSet}
        isMulti={false}
        placeholder={translateSingleString(locale, 'Type or select from list')}
        infiniteScrollProps={props.creditCardInfiniteScrollProps}
        isStatic={props.showTaskTable}
        selectedOptions={selectedOptions}
      />
      {!props.showTaskTable && (
        <Fragment>
          <CsvInputField
            css={css`
              margin-top: 30px;
            `}
            value={props.csvProps.value}
            onDrop={(files: File[]): void => {
              props.csvProps.onChange(files[0])
            }}
            errors={props.csvProps.errors}
            loadingBulkTemplate={loadingBulkTemplate}
            templateOptions={downloadTemplateOptions}
            showConvertToCSVMessage
          />
          <p
            css={css`
              margin-top: 10px;
              padding: 0px 0px;
              ${getFontCss(
                12,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
            `}
          >
            <Trns
              source={
                'NOTE: In order to make formatting and editing easier, the templates are XLSX (Excel) documents and are best viewed in Excel. However, they must be exported and uploaded as CSV.'
              }
              expressions={{
                faq: (
                  <HoverableLink
                    underline
                    size={HoverableLinkSizes.Small}
                    to={faqUrl()}
                    ariaLabelSource={'FAQ on Multi-Card CSVs'}
                  >
                    <Trns source={'FAQ on Multi-Card Templates'} />
                  </HoverableLink>
                ),
              }}
            />
          </p>
        </Fragment>
      )}
      {props.showTaskTable && props.virtualCardPushTasks !== undefined && (
        <VirtualCardPushTaskTable
          css={css`
            margin-top: 30px;
          `}
          tasks={props.virtualCardPushTasks}
          currency={props.currency}
          isBillPay={props.isBillPay}
        />
      )}
    </Box>
  )
}

export default React.memo(CreditCardAndCsvBox)
